/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {CustomDateRangeProps, IDatePickerState} from './types';
import {
  datePickerDateValidations,
  getInvalidDateErrorMessages
} from './utils';

const DateRageMenuItemID = 'modular-search-daterange-picker';
const DateRangeSubmissionID = 'date-selection-lock';

const useStyles = makeStyles(() => ({
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400,
    width      : '100%',
    marginTop  : '-15px',
    whiteSpace : 'break-spaces'
  }
}));

/**
 * Renders JSX to select Dates between two ranges.
 *
 * ```typescript
 * import CustomDateRange from '@ion/features/src/search/CustomDateRange';
 *
 *  <CustomDateRange onSelection={onSelection}
 *    menuItemClass={classes.dateMenuItem}
 *    menuSubmissionWrapperClass={classes.menuSubmissionWrapperClass}/>
 * ```
 */
const CustomDateRange = ({
  onSelection,
  menuItemClass,
  menuSubmissionWrapperClass,
  setShowSuggestions,
  includeContainerStyles = false
}: CustomDateRangeProps) => {
  const classes                         = useStyles();
  const [isValidDates, setIsValidDates] = useState<boolean>(true);
  const [showError, setShowError]       = useState<boolean>(false);
  const [errorType, setErrorType]       = useState<string>('');
  const [date, setDate]                 = useState<IDatePickerState[]>([
    {
      open  : false,
      value : null
    },
    {
      open  : false,
      value : null
    }
  ]);

  useEffect(() => {
    setShowError(false);
    setErrorType('');
    const [start, end] = date;
    const {isValid, errorType} = datePickerDateValidations(start?.value, end?.value);
    setIsValidDates(isValid);
    setErrorType(errorType);
  }, [date]);

  const onToggle = useCallback(function(index: number) {
    return function() {
      const dateToUpdate = [...date];
      dateToUpdate[index].open = !dateToUpdate[index].open;
      setDate([...dateToUpdate]);
    };
  }, [date]);

  const onDateChange = useCallback(function(index: number) {
    return function(value) {
      const dateToUpdate = [...date];
      dateToUpdate[index].value = value;
      setDate([...dateToUpdate]);
    };
  }, []);

  const onCancel = useCallback(function() {
    setShowSuggestions(false);
  }, []);

  const onSubmit = useCallback(function(dateRanges: IDatePickerState[]) {
    return function(e) {
      if (!isValidDates) {
        setShowError(true);
      } else {
        setShowError(false);
        onSelection(dateRanges)(e);
      };
    };
  }, [isValidDates]);

  return (<MenuItem id={DateRageMenuItemID}
    classes={{root: menuItemClass}}
    key={'suggestion-date-daterange-picker'}>
    <DatePicker closeOnClickOutside
      useDatePickerTextField
      includeContainerStyles={includeContainerStyles}
      hasError={showError}
      placeholder={translate('common:modularsearch.date.start')}
      open={date[0].open}
      toggle={onToggle(0)}
      onChange={onDateChange(0)}
      defaultValue={date[0].value} />
    <DatePicker closeOnClickOutside
      useDatePickerTextField
      includeContainerStyles={includeContainerStyles}
      hasError={showError}
      placeholder={translate('common:modularsearch.date.end')}
      open={date[1].open}
      toggle={onToggle(1)}
      onChange={onDateChange(1)}
      defaultValue={date[1].value} />
    {showError && (
      <span className={classes.errorMessage}>{getInvalidDateErrorMessages(errorType)}</span>
    )}
    <div id={DateRangeSubmissionID} className={menuSubmissionWrapperClass}>
      <TertiaryButton type='submit' onClick={onCancel}>
        {translate('common:editors.dateTime.cancel')}
      </TertiaryButton>
      <PrimaryButton type='submit' onClick={onSubmit(date)}>
        {translate('common:modularsearch.date.select')}
      </PrimaryButton>
    </div>
  </MenuItem>);
};

export default CustomDateRange;
